// Agency resources: the prototype's Settings > Agency Resources ("Manage the quick links
// everyone can access from the Dashboard"). Everyone sees the links; management edits them.
import { useState, type FormEvent } from 'react';
import { Banner, useApiClient, useQuery } from '@acc/web-shared';
import { PageHead, useAgency } from '../agency';

export interface AgencyLink { linkId: string; label: string; url: string | null }

export function Resources() {
  const a = useAgency();
  const api = useApiClient();
  const q = useQuery<{ links: AgencyLink[] }>(a.path('/links'));
  const [editing, setEditing] = useState<AgencyLink | 'new' | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = { label: String(f.get('label') ?? ''), url: String(f.get('url') ?? '').trim() || null };
    setBusy(true); setError('');
    try {
      if (editing === 'new') await api(a.path('/links'), { method: 'POST', body });
      else if (editing) await api(a.path(`/links/${editing.linkId}`), { method: 'PATCH', body });
      setEditing(null); q.reload(); a.notify('ok', 'Resource saved');
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  async function remove(l: AgencyLink) {
    if (!window.confirm(`Remove "${l.label}" from the agency resources?`)) return;
    try { await api(a.path(`/links/${l.linkId}`), { method: 'DELETE' }); q.reload(); a.notify('ok', 'Resource removed'); }
    catch (err) { a.notify('bad', (err as Error).message); }
  }

  return (
    <>
      <PageHead title="Agency resources" sub={a.manager ? 'Manage the quick links everyone can access from the Dashboard. The first four show there.' : 'Links your agency uses every day.'}>
        {a.manager && !editing && <button type="button" className="btn primary" onClick={() => setEditing('new')}>+ Add Link</button>}
      </PageHead>
      {q.error && <Banner tone="bad">{q.error.message}</Banner>}
      {editing && (
        <form className="card" onSubmit={(e) => void save(e)} style={{ marginBottom: 16 }}>
          <h2 style={{ marginTop: 0, fontSize: 16 }}>{editing === 'new' ? 'Add resource' : 'Edit resource'}</h2>
          {error && <Banner tone="bad">{error}</Banner>}
          <div className="form-grid">
            <label className="field">Label<input name="label" required maxLength={100} defaultValue={editing === 'new' ? '' : editing.label} /></label>
            <label className="field">Website URL (https://)<input name="url" type="url" pattern="https://.*" placeholder="https://example.com" maxLength={2000} defaultValue={editing === 'new' ? '' : editing.url ?? ''} /></label>
          </div>
          <div className="toolbar" style={{ marginTop: 12 }}>
            <button type="button" className="btn" onClick={() => { setEditing(null); setError(''); }}>Cancel</button>
            <button className="btn primary" disabled={busy}>Save Link</button>
          </div>
        </form>
      )}
      {q.data && (q.data.links.length ? (
        <div className="links-grid">
          {q.data.links.map((l) => (
            <div key={l.linkId} className="card link-card">
              <strong>{l.label}</strong>
              {l.url ? <a href={l.url} target="_blank" rel="noopener noreferrer">Open link ↗<span className="sr-only"> {l.label} (opens in a new tab)</span></a> : <span className="muted">Link not configured</span>}
              {a.manager && (
                <div className="toolbar" style={{ margin: 0 }}>
                  <button type="button" className="btn small" onClick={() => setEditing(l)}>Edit<span className="sr-only"> {l.label}</span></button>
                  <button type="button" className="btn small danger" onClick={() => void remove(l)}>Remove<span className="sr-only"> {l.label}</span></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : <div className="card empty">{a.manager ? 'No resources yet. Add the portals and documents your team opens every day.' : 'Your agency has not added any resources yet.'}</div>)}
    </>
  );
}
